<template>
	<div class="geographic-breakdown gray-bar-top app">
		<h3 class="module-title">Geographic Breakdown</h3>
		<div class="tab-container">
			<a :class="{active: viewRegions}" class="sub-header" @click="changeTab('regions')">Regions</a>
			<span v-if="showCurrency">
				<a
					:class="{active: viewCurrency}"
					class="sub-header divider-line"
					@click="changeTab('currency')"
				>Currency Weighting</a>
				<i-ball-modal tag="CWEIGHT" />
			</span>
			<span v-if="showTopCountries">
				<a
					:class="{active: viewTopCountries}"
					class="sub-header divider-line"
					@click="changeTab('topCountries')"
				>Top Countries</a>
			</span>
		</div>
		<!-- Regions-->
		<div v-if="viewRegions" class="row">
			<div v-if="regionsData.length">
				<div class="col-md-6">
					<img :src="regionsChart">
				</div>
				<div class="col-md-6">
					<ul class="list-horizontal-bars">
						<li v-for="(region, index) in regionsData" :key="index" :class="{'top-border': index === 0}">
							<div class="label-container-blue-bar">
								<div class="label-value-blue-bar">
									{{ region.SubTypeName }}
									<disclosure-superscript
										v-if="region.SubTypeName == 'Cash & equivalents'"
										class="detail-page"
										anchor="geo_cashEquivalents"
										:statistics="['ALLCTRY_CECTRY']"
									/>
									<div
										class="blue-bar"
										:style="{ 'z-index': -1, 'width': calculateWidth(region.DataValue, regionsMax) + '%'}"
									/>
								</div>
							</div>

							<div class="data-value strong">{{ $format.rawPercent(region.DataValue, 1) }}</div>
						</li>
					</ul>
					<div class="sub-info adjust-down">% of net assets as of {{ regionsDate }} (updated monthly).</div>
				</div>
			</div>
			<div v-else class="no-data col-md-6">No regions data is available at this time.</div>
		</div>
		<!-- Currency Weighting-->
		<div v-if="viewCurrency" class="row">
			<div v-if="currencyData.length">
				<div class="col-md-6">
					<div class="currency-table">
						<div
							v-for="(currency, index) in currencyData"
							:key="index"
							:class="getCurrencyClass(currency.SubTypeName)"
						>
							{{ currency.SubTypeName }}
							<span class="data">{{ currency.DataValue }}%</span>
						</div>
					</div>
					<div class="sub-info adjust-down">Data as of {{ currencyDate }}</div>
				</div>
			</div>
			<div v-else class="no-data col-md-6">No currency data is available at this time</div>
		</div>
		<!-- Top Countries  -->
		<div v-if="viewTopCountries" class="row">
			<div v-if="countriesData.length">
				<div class="col-md-6">
					<img :src="countriesChart">
				</div>
				<div class="col-md-6">
					<ul class="list-horizontal-bars">
						<li
							v-for="(topCountry, index) in countriesData"
							:key="index"
							:class="{'top-border': index === 0}"
						>
							<div class="label-container-blue-bar">
								<div class="label-value-blue-bar">
									{{ topCountry.ValueDescription }}
									<div
										class="blue-bar"
										:style="{ 'z-index': -1, 'width': calculateWidth(topCountry.DataValue, countriesMax) + '%'}"
									/>
								</div>
							</div>

							<div class="data-value strong">{{ $format.rawPercent(topCountry.DataValue, 1) }}</div>
						</li>
					</ul>
					<div class="sub-info adjust-down">% of net assets as of {{ countriesDate }} (updated monthly).</div>
				</div>
			</div>
			<div v-else class="no-data col-md-6">No countries data is available at this time.</div>
		</div>
	</div>
</template>

<script>
import { mapGetters } from 'vuex';

export default {
	name: 'GeographicBreakDown',
	data() {
		this.viewRegions = true;
		this.viewCurrency = false;
		this.viewTopCountries = false;
		return {
			viewRegions: true,
			viewCurrency: false,
			viewTopCountries: false,
			regionsMax: 1,
			countriesMax: 1,
			currencyFunds: { VIGBF: true, VIHIB: true, VIBF: true }
		};
	},
	computed: {
		...mapGetters(['getFundAttributes', 'geographicBreakdownData']),
		countriesChart() {
			return (this.widgetData && this.widgetData.countriesChart) || [];
		},
		countriesData() {
			return (this.widgetData && this.widgetData.countriesData) || [];
		},
		countriesDate() {
			return (this.widgetData && this.widgetData.countriesDate) || '--';
		},
		currencyData() {
			return (this.widgetData && this.widgetData.currencyData) || [];
		},
		currencyDate() {
			return (this.widgetData && this.widgetData.currencyDate) || '--';
		},
		regionsChart() {
			return (this.widgetData && this.widgetData.regionsChart) || [];
		},
		regionsData() {
			return (this.widgetData && this.widgetData.regionsData) || [];
		},
		regionsDate() {
			return (this.widgetData && this.widgetData.regionsDate) || '--';
		},
		showCurrency() {
			return (this.widgetData && this.widgetData.showCurrency) || false;
		},
		showTopCountries() {
			return (
				(this.widgetData && this.widgetData.showTopCountries) || false
			);
		},
		widgetData() {
			const data = this.geographicBreakdownData;
			const isTargetDate =
				this.getFundAttributes.Objective === 'RetirementTargetDate';
			if (data && this.getFundAttributes) {
				data.showCurrency = this.currencyFunds[
					this.getFundAttributes.FundSymbol
				];
				data.showTopCountries = !isTargetDate;
				this.$EventBus.$emit(
					'ExcelData',
					'GeographicBreakdownModule',
					data
				);
			}
			return data;
		}
	},
	watch: {
		regionsData() {
			this.setMax(this.regionsData, 'regionsMax');
		},
		countriesData() {
			this.setMax(this.countriesData, 'countriesMax');
		}
	},
	created() {
		//Med and small breakpoint will load the data on page load but create the module when holdings is selected
		//Watch won't fire in this case
		if (this.regionsData.length) {
			this.setMax(this.regionsData, 'regionsMax');
		}
		if (this.countriesData.length) {
			this.setMax(this.countriesData, 'countriesMax');
		}
	},
	methods: {
		calculateWidth(value, maxValue) {
			let width = 0;
			if (value && maxValue) {
				width = (value / maxValue) * 90;
			}
			return width;
		},
		changeTab(view) {
			if (view === 'regions') {
				this.viewRegions = true;
				this.viewTopCountries = false;
				this.viewCurrency = false;
			}
			if (view === 'currency') {
				this.viewRegions = false;
				this.viewTopCountries = false;
				this.viewCurrency = true;
			}
			if (view === 'topCountries') {
				this.viewRegions = false;
				this.viewTopCountries = true;
				this.viewCurrency = false;
			}
		},
		getCurrencyClass(name) {
			let classString = 'sub-currency';
			if (name && name.includes('U.S. Dollar')) {
				classString = 'main-currency';
			}
			return classString;
		},
		//CG-15251: For geographic breakdown United States is always first but it isn't necessarily the max value
		//Look through the arrays to find the max value
		setMax(data, maxProperty) {
			let maxValue = 1;
			for (let i = 0; i < data.length; i++) {
				const value = data[i].DataValue * 1;
				if (value > maxValue) {
					maxValue = value;
				}
			}
			this[maxProperty] = maxValue;
		}
	}
};
</script>

<style lang="less" scoped>
@import '../../assets/css/variables.less';
.geographic-breakdown {
	.top-state-title {
		margin-bottom: 30px;
	}
	img {
		width: 100%;
	}
	.tab-container {
		margin-bottom: 20px;
	}
	.currency-table {
		.thin-light-gray-border-bottom();
		margin-bottom: 13px;
		width: 100%;
		.sub-currency {
			color: @dark-gray;
			font-family: 'AvenirNextLTW01-Regular';
			font-size: 12px;
			height: 22px;
			margin-left: 6px;
		}
		.main-currency {
			color: @dark-gray;
			font-family: 'Avenir next lt w01 demi';
			font-size: 14px;
			.module-border-top();
		}
		.data {
			float: right;
		}
	}
}
.as-of.adjust-down {
	margin-top: 10px;
}
</style>
